import type { FocusEvent } from 'react';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

import type { QuestionRendererProps, SingleChoiceValue } from '../types';

const OTHER_OPTION_ID = '__other__';

const normalizeValue = (value: SingleChoiceValue | undefined): SingleChoiceValue => {
  if (!value) {
    return { optionId: undefined, otherText: undefined };
  }
  return {
    optionId: value.optionId,
    otherText: value.otherText,
  };
};

export const MultipleChoiceSingleQuestion = ({
  question,
  value,
  onChange,
  onBlur,
  error,
  mode,
  disabled,
}: QuestionRendererProps<SingleChoiceValue>) => {
  if (question.type !== 'multiple_choice_single') {
    return null;
  }

  const isInteractive = mode === 'runtime' && !disabled;
  const normalized = normalizeValue(value);
  const selectedOptionId = normalized.optionId ?? (normalized.otherText ? OTHER_OPTION_ID : undefined);
  const otherInputId = `${question.id}-other`;

  const handleValueChange = (nextValue: string) => {
    if (!isInteractive) {
      return;
    }

    if (nextValue === OTHER_OPTION_ID) {
      onChange?.({ optionId: undefined, otherText: normalized.otherText ?? '' });
      onBlur?.();
      return;
    }

    onChange?.({ optionId: nextValue, otherText: undefined });
    onBlur?.();
  };

  const handleOtherChange = (next: string) => {
    if (!isInteractive) {
      return;
    }

    onChange?.({ optionId: next ? undefined : normalized.optionId, otherText: next });
  };

  return (
    <div
      onBlur={(event: FocusEvent<HTMLDivElement>) => {
        if (!onBlur) {
          return;
        }
        const nextFocus = event.relatedTarget as Node | null;
        if (nextFocus && event.currentTarget.contains(nextFocus)) {
          return;
        }
        onBlur();
      }}
    >
      <RadioGroup
        value={selectedOptionId}
        onValueChange={handleValueChange}
        aria-invalid={Boolean(error)}
        aria-describedby={`validation-${question.id}`}
        aria-errormessage={error ? `error-${question.id}` : undefined}
      >
        {question.options.map((option) => {
          const inputId = `${question.id}-${option.id}`;
          return (
            <Label
              key={option.id}
              htmlFor={inputId}
              className={`flex items-start gap-3 rounded-lg border border-border/60 bg-background px-3 py-2.5 text-inherit font-normal leading-normal ${
                isInteractive ? 'cursor-pointer hover:border-primary/70' : 'cursor-not-allowed'
              }`}
            >
              <RadioGroupItem id={inputId} value={option.id} disabled={!isInteractive} />
              <div className="flex-1">
                <span className="text-sm font-medium text-foreground">{option.label}</span>
                {option.description ? <p className="mt-1 text-xs text-muted-foreground">{option.description}</p> : null}
              </div>
            </Label>
          );
        })}
        {question.allowOther ? (
          <div className="space-y-2 rounded-lg border border-border/60 bg-background px-3 py-2.5">
            <Label
              htmlFor={otherInputId}
              className={`flex items-start gap-3 text-inherit font-normal leading-normal ${
                isInteractive ? 'cursor-pointer' : 'cursor-not-allowed'
              }`}
            >
              <RadioGroupItem id={otherInputId} value={OTHER_OPTION_ID} disabled={!isInteractive} />
              <div className="flex-1">
                <span className="text-sm font-medium text-foreground">Other</span>
                <p className="mt-1 text-xs text-muted-foreground">Let respondents provide their own answer.</p>
              </div>
            </Label>
            <Input
              value={normalized.otherText ?? ''}
              onChange={(event) => handleOtherChange(event.target.value)}
              placeholder="Please specify"
              disabled={!isInteractive || selectedOptionId !== OTHER_OPTION_ID}
              aria-describedby={`validation-${question.id}`}
              aria-invalid={Boolean(error)}
            />
          </div>
        ) : null}
      </RadioGroup>
    </div>
  );
};
